import React, { useState, useEffect, useRef } from 'react';

interface CustomInputBaseProps {
  value?: string;
  id?: string;
  className?: string;
  label: string;
}

interface CustomDateInputProps extends CustomInputBaseProps {
  onClick?: () => void;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  type?: 'date';
}

interface CustomTextAreaProps extends CustomInputBaseProps {
  onChange?: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLTextAreaElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLTextAreaElement>) => void;
  minHeight?: string;
  type: 'textarea';
}

type CustomInputProps = CustomDateInputProps | CustomTextAreaProps;

const isTextAreaProps = (props: CustomInputProps): props is CustomTextAreaProps => {
  return props.type === 'textarea';
};

const CustomInput = React.forwardRef<HTMLInputElement | HTMLTextAreaElement, CustomInputProps>(
  (props, ref) => {
    const [isFocused, setIsFocused] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    
    // Common click outside handler for both types
    useEffect(() => {
      const handleOutsideClick = (event: MouseEvent) => {
        if (
          containerRef.current && 
          !containerRef.current.contains(event.target as Node) && 
          isFocused && 
          !props.value
        ) {
          setIsFocused(false);
        }
      };

      document.addEventListener('mousedown', handleOutsideClick);
      return () => {
        document.removeEventListener('mousedown', handleOutsideClick);
      };
    }, [isFocused, props.value]);

    // Render logic for textarea
    if (isTextAreaProps(props)) {
      const { value, onChange, onFocus, onBlur, id, className, label, minHeight = '48px' } = props;
      
      const handleTextAreaFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
        setIsFocused(true);
        if (onFocus) onFocus(e);
      };
      
      const handleTextAreaBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
        setIsFocused(false);
        if (onBlur) onBlur(e);
      };

      const shouldShowFloatingLabel = value || isFocused;

      return (
        <div className="relative" ref={containerRef}>
          <div 
            className={`w-full rounded-lg border border-gray-300 bg-white transition-all duration-100 flex items-center px-4 ${className || ''}`}
            style={{ minHeight }}
            onClick={() => setIsFocused(true)}
          >
            <div 
              className={`absolute left-4 pointer-events-none transform-gpu font-buenos-aires transition-all ${
                shouldShowFloatingLabel 
                  ? 'top-0 -translate-y-1/2 text-xs font-medium text-black bg-white px-1 -ml-1' 
                  : 'text-base text-black opacity-60'
              }`}
              style={{
                willChange: 'transform, font-size',
                transition: 'transform 120ms ease-out, font-size 120ms ease-out, top 120ms ease-out'
              }}
            >
              {label}
            </div>
            
            <textarea
              value={value}
              onChange={onChange}
              onFocus={handleTextAreaFocus}
              onBlur={handleTextAreaBlur}
              id={id}
              ref={ref as React.RefObject<HTMLTextAreaElement>}
              className={`w-full bg-transparent border-none resize-none focus:outline-none font-buenos-aires text-base ${
                shouldShowFloatingLabel ? 'pt-4 pb-2' : 'py-3'
              }`}
              style={{ 
                minHeight: 'calc(100% - 12px)',
                display: 'block'
              }}
            />
          </div>
        </div>
      );
    }
    
    // Render logic for date input
    else {
      const { value, onClick, onChange, onBlur, onFocus, id, className, label } = props;
      
      const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        setIsFocused(true);
        if (onFocus) onFocus(e);
      };
      
      const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        setIsFocused(false);
        if (onBlur) onBlur(e);
      };

      const handleClick = (e: React.MouseEvent) => {
        setIsFocused(true);
        if (onClick) onClick();
      };

      const shouldShowFloatingLabel = value || isFocused;

      return (
        <div className="relative" ref={containerRef}>
          <div 
            className={`w-full h-12 border border-gray-300 rounded-lg bg-white cursor-pointer hover:border-gray-400 transition-all duration-100 flex items-center px-4 ${className || ''}`}
            onClick={handleClick}
          >
            <div 
              className={`absolute left-4 pointer-events-none transform-gpu font-buenos-aires transition-all ${
                shouldShowFloatingLabel 
                  ? 'top-0 -translate-y-1/2 text-xs font-medium text-black bg-white px-1 -ml-1' 
                  : 'text-base text-black opacity-60'
              }`}
              style={{
                willChange: 'transform, font-size',
                transition: 'transform 120ms ease-out, font-size 120ms ease-out, top 120ms ease-out'
              }}
            >
              {label}
            </div>
            
            {value && (
              <div className="pt-2 font-buenos-aires text-base">
                {value}
              </div>
            )}
          </div>
          <input
            type="text"
            value={value}
            onChange={onChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            id={id}
            className="sr-only"
            ref={ref as React.RefObject<HTMLInputElement>}
            readOnly
            aria-label={label}
          />
        </div>
      );
    }
  }
);

// For backwards compatibility
const CustomDateInput = React.forwardRef<HTMLInputElement, Omit<CustomDateInputProps, 'type'>>((props, ref) => {
  return <CustomInput {...props} ref={ref as React.RefObject<HTMLInputElement>} />;
});

// Alias for CustomTextArea for backwards compatibility
const CustomTextArea = React.forwardRef<HTMLTextAreaElement, Omit<CustomTextAreaProps, 'type'>>((props, ref) => {
  return <CustomInput {...props} type="textarea" ref={ref as React.RefObject<HTMLTextAreaElement>} />;
});

export { CustomTextArea };
export default CustomDateInput;
